package com.islamicmedia.shared.presentation

/**
 * EVERY word the referral program shows a user, in one place, for both the mobile app
 * (composeApp) and the website (site). Edit the wording here and both platforms change together;
 * neither UI should hard-code a referral string of its own.
 *
 * The strings in the first three groups are the owner's draft (2026-09-23). BODY, [shareText] and
 * APPLIED_CHIP follow the final rules (2026-09-24): a friend who subscribes with a code pays their
 * first month, whatever it costs, and their SECOND month is free at once (their next bill moves a
 * month later), and the member who shared the code gets a free month at the same moment.
 * APPLIED_CHIP itself is the owner's exact wording from a 2026-09-25 report (a friend who saw it
 * still had their referral voided at payment, told nothing): it says only that the code was
 * entered, not what it does, since that isn't decided until the friend actually pays. The
 * cancel-flow warning ([CANCEL_WARNING_TITLE], [cancelWarning]) is a draft too; the owner may edit
 * it. The "UI chrome" group was added so no screen has to invent text (a Cancel button, a retry,
 * the paused state); edit those freely too. No em dashes in user-facing copy.
 *
 * Admin pushes and admin-screen text are not here: they are server-side or admin-only.
 */
object ReferralCopy {

    // ── Menu and the "Refer a friend" screen ──────────────────────────────────────────────────
    const val MENU_ITEM = "🎁 Free Gift 🎁"
    const val TITLE = "🎁 Free Gift 🎁"
    const val BODY = "Share your code. When a friend subscribes, their second month is free, and you get a free month too."
    const val COPY_BUTTON = "Copy link"
    const val SHARE_BUTTON = "Share"
    const val COPIED = "Link copied"
    const val YOUR_CODE = "Your code"
    const val STAT_JOINED = "Friends joined"
    const val STAT_EARNED = "Free months earned"


    /**
     * e.g. "Your next free month unlocks on Oct 7, 2026". Pass ReferralStats.nextReleaseDateLabel().
     * Nothing waits to unlock any more (a referrer's month is given the moment their friend pays),
     * so the server sends no date and both screens hide this line; it stays for the contract.
     */
    fun nextRelease(date: String) = "Your next free month unlocks on $date"

    /** The message the Share button sends, with the member's code and link at the end. */
    fun shareText(code: String, link: String) =
        "I use Fitrah Filter to check movies, shows, books and games before my kids see them. Use my code $code when you subscribe and your second month is free: $link"

    const val NOT_ELIGIBLE = "Referrals are for subscribers. Subscribe to get your own code."

    // ── Entering a friend's code (paywall link, dialog, applied chip) ───────────────────────────
    const val PAYWALL_LINK = "Have a referral code?"
    const val DIALOG_TITLE = "Enter referral code"
    const val DIALOG_APPLY = "Apply"
    const val APPLIED_CHIP = "Referral code added"

    // ── Entering a friend's code AFTER subscribing (Free Gift screen, POST /api/referral/redeem) ──
    // Shown only while GET /api/referral/me says can_redeem: a new subscriber, still in their first
    // month, who hasn't already used a code (typically a ThriveCart buyer, or a main-account
    // subscriber who skipped the paywall's "Have a referral code?"). Reuses the same dialog look as
    // [DIALOG_TITLE] above, with its own title.
    const val REDEEM_LINK = "Got a code from a friend?"
    const val REDEEM_TITLE = "Enter your friend's code"

    /** [date] is the member's new next-bill date, e.g. "Nov 24, 2026" (ReferralRedeemResult.nextBill,
     *  formatted). */
    fun redeemSuccess(date: String) = "You're all set! Your next bill is now $date."

    /** Same success, when the server didn't send a date yet (the month is being granted in the
     *  background, or by hand). */
    const val REDEEM_SUCCESS_NO_DATE = "You're all set! Your free month has been added."

    // ── The one-time "Did a friend send you a code?" popup (owner's draft, 2026-09-25) ──────────
    // Shown on the home/search screen, at most once ever per account+backend (a device-side
    // marker — see SettingsStorage.hasSeenReferralRedeemPrompt), to a member a FRESH GET
    // /api/referral/me says canRedeem for. "Enter code" opens the exact same dialog as
    // [REDEEM_LINK] above (title [REDEEM_TITLE]); "No thanks" just closes the popup — the Free
    // Gift screen keeps offering the same entry until their first month ends.
    const val REDEEM_PROMPT_TITLE = "Did a friend send you a code?"
    const val REDEEM_PROMPT_BODY = "Enter it now and your next bill moves out a month."
    const val REDEEM_PROMPT_ENTER = "Enter code"
    const val REDEEM_PROMPT_DISMISS = "No thanks"

    // ── The one-time "your referral was blocked" notice (owner's report, 2026-09-25) ────────────
    // A friend typed a code, saw [APPLIED_CHIP], and at their first payment the server voided the
    // referral (e.g. their card matched another referred member's) — and they were told nothing.
    // Shown on the home/search screen (never together with [REDEEM_PROMPT_TITLE]'s popup), once per
    // device per block (GET /api/referral/me's referral_blocked / referral_blocked_at; see
    // MediaCheckerViewModel and SettingsStorage.hasAcknowledgedReferralBlocked). "OK" just closes
    // it; there is nothing to enter here, unlike [REDEEM_PROMPT_TITLE].
    const val REFERRAL_BLOCKED = "Your referral code couldn't be used for this purchase."
    const val REFERRAL_BLOCKED_OK = "OK"

    // ── The cancel flow, for a member whose referral free month is still at risk ──────────────
    // Shown on every step before they confirm (the save offers and the final "Cancel your
    // subscription?"), while they are still inside the first month of a referral: cancelling
    // then takes the free month back (and the friend who referred them loses theirs).

    const val CANCEL_WARNING_TITLE = "You'll lose your free month"

    /** [date] is when their next bill will be if they stay, e.g. "Nov 20, 2026". */
    fun cancelWarning(date: String) =
        "You're still in your first month, so cancelling now removes the free month from your referral. Stay subscribed and your next bill won't be until $date."

    // ── Errors (see [errorText] for which status shows which) ────────────────────────────────
    const val ERR_NOT_FOUND = "That code doesn't exist. Check it and try again."
    const val ERR_SELF = "You can't use your own code."
    const val ERR_NOT_NEW = "Referral codes are for new members."
    const val ERR_ALREADY = "A referral code is already applied to your account."
    const val ERR_INELIGIBLE = "This code isn't active right now."
    const val ERR_RATE = "Too many tries. Please wait a bit and try again."
    const val ERR_GENERIC = "Something went wrong. Please try again."
    // Redeem-only verdicts (POST /api/referral/redeem; see [errorText]).
    const val ERR_TOO_LATE = "Codes can only be used during your first month."
    const val ERR_NOT_SUBSCRIBED = "Subscribe first, then enter your friend's code here."
    const val ERR_ALREADY_REFERRED = "You've already used a friend's code."

    // ── UI chrome (added beyond the draft so no screen invents its own text) ──────────────────
    /** Placeholder inside the code field of the dialog. */
    const val DIALOG_PLACEHOLDER = "Enter code"
    const val DIALOG_CANCEL = "Cancel"
    /** The retry button when the referral screen couldn't load. */
    const val RETRY = "Try again"
    /** The referral screen when the program is switched off server-side (status "disabled"). */
    const val UNAVAILABLE = "Referrals aren't available right now."
    /**
     * The small line under [APPLIED_CHIP] on the public /register page only. That page checks the
     * code before it knows who is buying (it must not reveal whether an address is a member), and a
     * returning member's payment then gets no free month, so the page says so up front. Same words
     * as [ERR_NOT_NEW] to start with; the in-app paywall doesn't need it (its code was checked
     * against the signed-in account).
     */
    const val APPLIED_NEW_MEMBERS_ONLY = "Referral codes are for new members."

    /**
     * The sentence for an attach/redeem verdict (MediaCheckerUiState.referralCodeError or
     * .redeemCodeError), or null for null. Unknown statuses read as [ERR_GENERIC], so a new server
     * verdict never shows raw. Shared by both dialogs: "not_new" and "referrer_ineligible" mean the
     * same thing whichever one is open, and only "already_attached" / "already_referred" and
     * "too_late" / "not_subscribed" are specific to one. "blocked" (POST /api/referral/redeem, a
     * card match voided at payment) reads as [REFERRAL_BLOCKED], the same sentence as the one-time
     * notice for a code that was blocked earlier, outside this dialog.
     */
    fun errorText(status: String?): String? = when (status) {
        null -> null
        "not_found" -> ERR_NOT_FOUND
        "self" -> ERR_SELF
        "not_new" -> ERR_NOT_NEW
        "already_attached" -> ERR_ALREADY
        "already_referred" -> ERR_ALREADY_REFERRED
        "too_late" -> ERR_TOO_LATE
        "not_subscribed" -> ERR_NOT_SUBSCRIBED
        "referrer_ineligible" -> ERR_INELIGIBLE
        "rate_limited" -> ERR_RATE
        "blocked" -> REFERRAL_BLOCKED
        else -> ERR_GENERIC
    }
}
